import { useElements, useStripe } from "@stripe/react-stripe-js"
import { clearLocalStorage } from "../stores/local_storage"
import { Designation, State, StripeToken, Total } from "../types"
import { FormSubmission } from "../utils/FormSubmission"
import { FormSubmitDisplay } from "./display"
import { useNavigate } from "react-router-dom"
import { useContext } from "react"
import { ServerRenderedProps } from "source/shared/contexts/ServerRenderedProps"
import { createStripeConfirmationToken } from "../queries"
import { RetryableDonationFormInvariantError } from "../errors"
import { useDonationFormContext } from "../hooks/useDonationFormContext"
import { validate, hasErrors } from "../stores/validations"

type FormSubmitProps = {
  state: State
  totalCents: Total
  calculatedDesignations: Designation[]
}

export function FormSubmit({
  state,
  totalCents,
  calculatedDesignations,
}: FormSubmitProps) {
  const {
    current_person: {
      data: {
        attributes: { email_address: currentPersonEmail },
      },
    },
    current_organization: { id: currentOrganizationId },
  } = useContext(ServerRenderedProps)
  const { config, dispatch } = useDonationFormContext()
  const navigate = useNavigate()
  const stripe = useStripe()
  const stripeElements = useElements()

  const obtainStripeToken = async (): Promise<StripeToken> => {
    if (!stripe || !stripeElements) {
      throw new RetryableDonationFormInvariantError()
    } else if (state.stripePaymentMethod?.id) {
      return state.stripePaymentMethod.id
    } else {
      const stripeConfirmationToken = await createStripeConfirmationToken({
        stripe,
        stripeElements,
        billingEmail: currentPersonEmail,
        organizationId: currentOrganizationId,
      })
      return stripeConfirmationToken.id
    }
  }

  const handleSubmit = async () => {
    // Need to check for errors now, not wait for revalidation
    const currentErrors = validate(state, config)
    if (hasErrors(currentErrors)) {
      // Make sure the errors get displayed
      dispatch({ type: "VALIDATE", config })
      return
    }

    try {
      const stripeToken = await obtainStripeToken()
      const submission = FormSubmission.create({
        state,
        calculatedDesignations,
        stripeToken,
      })
      await submission.submit()
      clearLocalStorage()
      navigate("donation_thank_you")
    } catch (error) {
      alert(`Error: ${error}`)
    }
  }

  // TODO: hook this up (or something similar) to the submit button to disable
  // `handleSubmit` from firing when this is true
  // const disabled = hasErrors(state.errors)

  return <FormSubmitDisplay {...{ handleSubmit, totalCents }} />
}
